import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from '@emotion/styled';
import SmallModalLayout from '../structure/SmallModalLayout';
import Button from '../general/Button';
import { style_border_default } from '../../constants/stylesValues';
import { FoodOrderingFeatureFlagId, HealthcareSchedulingFeatureFlagId } from '../../constants/featureFlagIds';
import ApplicationModel from '../../models/applications/api/ApplicationModel';
import {
    color_colors_growth,
    color_colors_decline,
    color_text_light,
    color_text_default,
    color_shades_light,
    color_shades_lighter,
    color_shades_darker,
    color_defaults_white
} from '../../constants/colors';
import ModalHeader from '../general/ModalHeader';
import FooterBar from '../structure/FooterBar';
import Switch from '../forms/Switch';
import { css } from '@emotion/css';
import { useTagConfig } from '../../hooks/TagConfigContainer';

interface TagFilterModalProps {
    isVisible: boolean;
    onClose: () => void;
    tags: string[];
    tagsForDisplay: string[];
    selectedTags: Set<string>;
    excludedTags: Set<string>;
    onToggle: (tag: string, mode: 'include' | 'exclude') => void;
    onApply: (includeMode?: 'AND' | 'OR') => void | Promise<void>;
    isLoading?: boolean;
    isCrossOrgMode?: boolean;
    application?: ApplicationModel;
    setFilterLogic?: (includeMode?: 'AND' | 'OR') => void;
}

const TAGS_TO_FILTER_OUT = [];

export const TagFilterModal: React.FC<TagFilterModalProps> = React.memo(({
    isVisible,
    onClose,
    tags,
    tagsForDisplay: originalTagsForDisplay,
    selectedTags,
    excludedTags,
    onToggle,
    onApply,
    isLoading = false,
    isCrossOrgMode = false,
    application,
    setFilterLogic
}) => {
    const { foodOrderingTags, healthcareTags, loadTagConfig } = useTagConfig();
    const [localSelectedTags, setLocalSelectedTags] = useState<Set<string>>(new Set());
    const [localExcludedTags, setLocalExcludedTags] = useState<Set<string>>(new Set());
    const [currentPage, setCurrentPage] = useState(1);
    const [isApplying, setIsApplying] = useState(false);
    const [includeMode, setIncludeMode] = useState<'AND' | 'OR'>('AND');

    const TAGS_PER_PAGE = 50; // Show 50 tags per page for better performance

    useEffect(() => {
        if (application?.id) {
            loadTagConfig(application.id);
        }
    }, [application?.id]);

    useEffect(() => {
        if (isVisible) {
            setLocalSelectedTags(new Set(selectedTags));
            setLocalExcludedTags(new Set(excludedTags));
            setCurrentPage(1);
        }
    }, [isVisible, selectedTags, excludedTags]);

    useEffect(() => {
        setFilterLogic(includeMode);
    }, [includeMode]);

    const handleIncludeToggle = useCallback((tag: string) => {
        setLocalSelectedTags(prev => {
            const newSet = new Set(prev);
            if (newSet.has(tag)) {
                newSet.delete(tag);
            } else {
                newSet.add(tag);
                setLocalExcludedTags(prevExcluded => {
                    const newExcludedSet = new Set(prevExcluded);
                    newExcludedSet.delete(tag);
                    return newExcludedSet;
                });
            }
            return newSet;
        });
    }, []);

    const handleExcludeToggle = useCallback((tag: string) => {
        setLocalExcludedTags(prev => {
            const newSet = new Set(prev);
            if (newSet.has(tag)) {
                newSet.delete(tag);
            } else {
                newSet.add(tag);
                setLocalSelectedTags(prevSelected => {
                    const newSelectedSet = new Set(prevSelected);
                    newSelectedSet.delete(tag);
                    return newSelectedSet;
                });
            }
            return newSet;
        });
    }, []);

    const filteredTagPairs = useMemo(() => {
        let pairs = tags
            .map((tag, idx) => ({
                tag,
                tagDisplay: originalTagsForDisplay[idx],
            }));

        if (!isCrossOrgMode) {
            // Filter out Customer Delight tags
            pairs = pairs.filter(({ tagDisplay }) => !TAGS_TO_FILTER_OUT.includes(tagDisplay));

            // Filter based on application type
            if (application?.featureFlags?.some(flag => flag.id === FoodOrderingFeatureFlagId)) {
                // For foodOrdering, exclude healthcare-only tags
                pairs = pairs.filter(({ tag }) => foodOrderingTags.includes(tag));
            } else if (application?.featureFlags?.some(flag => flag.id === HealthcareSchedulingFeatureFlagId)) {
                // For healthcare, exclude foodOrdering-only tags
                pairs = pairs.filter(({ tag }) => healthcareTags.includes(tag));
            }
        }

        return pairs;
    }, [tags, originalTagsForDisplay, isCrossOrgMode, application?.featureFlags, foodOrderingTags, healthcareTags]);

    const sortedTags = useMemo(() => {
        const tagPairs = filteredTagPairs.map(({ tag, tagDisplay }) => ({
            tag,
            tagDisplay,
            sortKey: (tagDisplay || tag).toLowerCase()
        }));
        return tagPairs.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
    }, [filteredTagPairs]);

    const paginationInfo = useMemo(() => {
        const totalPages = Math.ceil(sortedTags.length / TAGS_PER_PAGE);
        const startIndex = (currentPage - 1) * TAGS_PER_PAGE;
        const endIndex = Math.min(startIndex + TAGS_PER_PAGE, sortedTags.length);
        const currentPageTags = sortedTags.slice(startIndex, endIndex);

        return {
            totalPages,
            currentPageTags,
            totalItems: sortedTags.length,
            startIndex: startIndex + 1,
            endIndex
        };
    }, [sortedTags, currentPage]);

    const handleApply = useCallback(async () => {
        setIsApplying(true);

        try {
            // Batch all changes to minimize re-renders
            const selectedChanges: Array<{ tag: string; action: 'add' | 'remove' }> = [];
            const excludedChanges: Array<{ tag: string; action: 'add' | 'remove' }> = [];

            for (const tag of localSelectedTags) {
                if (!selectedTags.has(tag)) {
                    selectedChanges.push({ tag, action: 'add' });
                }
            }
            for (const tag of selectedTags) {
                if (!localSelectedTags.has(tag)) {
                    selectedChanges.push({ tag, action: 'remove' });
                }
            }

            for (const tag of localExcludedTags) {
                if (!excludedTags.has(tag)) {
                    excludedChanges.push({ tag, action: 'add' });
                }
            }
            for (const tag of excludedTags) {
                if (!localExcludedTags.has(tag)) {
                    excludedChanges.push({ tag, action: 'remove' });
                }
            }

            // Apply all changes in batches to reduce re-renders
            selectedChanges.forEach(({ tag }) => onToggle(tag, 'include'));
            excludedChanges.forEach(({ tag }) => onToggle(tag, 'exclude'));

            await onApply(includeMode);
            onClose();
        } catch (error) {
            console.error('Error applying tag filters:', error);
        } finally {
            setIsApplying(false);
        }
    }, [localSelectedTags, localExcludedTags, selectedTags, excludedTags, onToggle, onApply, onClose, includeMode]);

    const handleReset = useCallback(() => {
        setLocalSelectedTags(new Set());
        setLocalExcludedTags(new Set());
    }, []);

    const hasChanges = useMemo(() => {
        const selectedEqual = localSelectedTags.size === selectedTags.size &&
            Array.from(localSelectedTags).every(tag => selectedTags.has(tag));
        const excludedEqual = localExcludedTags.size === excludedTags.size &&
            Array.from(localExcludedTags).every(tag => excludedTags.has(tag));
        return !selectedEqual || !excludedEqual;
    }, [localSelectedTags, localExcludedTags, selectedTags, excludedTags]);

    return (
        <SmallModalLayout
            isVisible={isVisible}
            onClose={onClose}
            className={modalContentStyle}
            innerClassName={modalInnerStyle}
        >
            <ModalHeader title="Filter Calls by Tags" onClose={onClose} />
            <ModalBody>
                {isLoading || isApplying ? (
                    <LoadingContainer>
                        <LoadingSpinner />
                        <LoadingText>{isApplying ? 'Applying filters...' : 'Loading tags...'}</LoadingText>
                    </LoadingContainer>
                ) : (
                    <ScrollContainer>
                        <FilterSections>
                            <IncludeFilterSection>
                                <SectionHeader>
                                    <SectionTitleRow>
                                        <SectionTitle>
                                            <IncludeIcon>✓</IncludeIcon>
                                            Include Tags
                                            <ModeSelector>
                                                <ModeLabel>AND</ModeLabel>
                                                <Switch
                                                    checked={includeMode === 'OR'}
                                                    onChange={(checked) => setIncludeMode(checked ? 'OR' : 'AND')}
                                                    disabled={isApplying}
                                                />
                                                <ModeLabel>OR</ModeLabel>
                                            </ModeSelector>
                                        </SectionTitle>
                                    </SectionTitleRow>
                                    <SectionSubtitle>
                                        {includeMode === 'AND'
                                            ? 'Show calls that have ALL of these tags'
                                            : 'Show calls that have ANY of these tags'
                                        }
                                    </SectionSubtitle>
                                </SectionHeader>
                                <TagsList position='left'>
                                    {paginationInfo.currentPageTags.length === 0 ? (
                                        <NoTagsMessage>No tags available.</NoTagsMessage>
                                    ) : (
                                        paginationInfo.currentPageTags.map(({ tag, tagDisplay }) => {
                                            const isSelected = localSelectedTags.has(tag);
                                            const isDisabled = localExcludedTags.has(tag);
                                            return (
                                                <TagCheckboxItem
                                                    key={`include-${tag}`}
                                                    onClick={() => !isDisabled && handleIncludeToggle(tag)}
                                                    disabled={isDisabled}
                                                    selected={isSelected}
                                                >
                                                    <Checkbox
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        disabled={isDisabled}
                                                        readOnly
                                                    />
                                                    <TagText selected={isSelected} disabled={isDisabled}>
                                                        {tagDisplay}
                                                    </TagText>
                                                </TagCheckboxItem>
                                            );
                                        })
                                    )}
                                </TagsList>
                            </IncludeFilterSection>

                            <ExcludeFilterSection>
                                <SectionHeader>
                                    <SectionTitle>
                                        <ExcludeIcon>✗</ExcludeIcon>
                                        Exclude Tags
                                    </SectionTitle>
                                    <SectionSubtitle>Hide calls that have these tags</SectionSubtitle>
                                </SectionHeader>
                                <TagsList position='right'>
                                    {paginationInfo.currentPageTags.length === 0 ? (
                                        <NoTagsMessage>No tags available.</NoTagsMessage>
                                    ) : (
                                        paginationInfo.currentPageTags.map(({ tag, tagDisplay }) => {
                                            const isSelected = localExcludedTags.has(tag);
                                            const isDisabled = localSelectedTags.has(tag);
                                            return (
                                                <TagCheckboxItem
                                                    key={`exclude-${tag}`}
                                                    onClick={() => !isDisabled && handleExcludeToggle(tag)}
                                                    disabled={isDisabled}
                                                    selected={isSelected}
                                                >
                                                    <Checkbox
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        disabled={isDisabled}
                                                        readOnly
                                                    />
                                                    <TagText selected={isSelected} disabled={isDisabled}>
                                                        {tagDisplay}
                                                    </TagText>
                                                </TagCheckboxItem>
                                            );
                                        })
                                    )}
                                </TagsList>
                            </ExcludeFilterSection>
                        </FilterSections>

                        {paginationInfo.totalItems > TAGS_PER_PAGE && (
                            <PaginationFooter>
                                <PaginationControls>
                                    <PaginationButton
                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                    >
                                        Previous
                                    </PaginationButton>
                                    <PageInfo>
                                        {currentPage} / {paginationInfo.totalPages}
                                    </PageInfo>
                                    <PaginationButton
                                        onClick={() => setCurrentPage(p => Math.min(paginationInfo.totalPages, p + 1))}
                                        disabled={currentPage === paginationInfo.totalPages}
                                    >
                                        Next
                                    </PaginationButton>
                                </PaginationControls>
                            </PaginationFooter>
                        )}
                    </ScrollContainer>
                )}
            </ModalBody>

            <FooterBar>
                <ButtonsWrapper>

                    <ResetButton
                        themes={['white-small']}
                        text="Reset All"
                        onClick={handleReset}
                        disabled={(localSelectedTags.size === 0 && localExcludedTags.size === 0) || isApplying}
                    />
                    <CancelAndApplyButtons>
                        <CancelButton
                            themes={['white-small']}
                            text="Cancel"
                            onClick={onClose}
                            disabled={isApplying}
                        />
                        <ApplyButton
                            themes={['primary']}
                            text="Apply Filters"
                            onClick={handleApply}
                            disabled={!hasChanges || isApplying}
                            loading={isApplying}
                        />
                    </CancelAndApplyButtons>

                </ButtonsWrapper>

            </FooterBar >
        </SmallModalLayout >
    );
});

TagFilterModal.displayName = 'TagFilterModal';

const ButtonsWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
`;

const modalContentStyle = css`
    width: 100%;
    max-width: 900px;
    height: 80vh;
    max-height: 80vh;

    @media (max-width: 768px) {
        width: 98%;
        max-width: 100%;
        height: 90vh;
        max-height: 90vh;
    }
`;

const CancelAndApplyButtons = styled.div``;

const modalInnerStyle = css`
    display: flex;
    flex-direction: column;
    height: 100%;
`;

const ModalBody = styled.div`
    padding: 24px;
    padding-right: 24px;
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    position: relative;
    min-height: 0; /* Fix for flex overflow */
`;

const ScrollContainer = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    position: relative;
    min-height: 0; /* Fix for flex overflow */
    height: 100%; /* Take full available height */
`;

const FilterSections = styled.div`
    display: flex;
    flex-direction: row;
    gap: 12px;
    flex: 1;
    margin-bottom: 76px;
    min-height: 0; /* Fix for flex overflow */
    height: 100%; /* Take full available height */

    @media (max-width: 768px) {
        flex-direction: column;
    }
`;

const IncludeFilterSection = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    min-height: 0; /* Fix for flex overflow */
    max-height: 100%; /* Constrain to parent height */
`;

const ExcludeFilterSection = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    min-height: 0; /* Fix for flex overflow */
    max-height: 100%; /* Constrain to parent height */
`;

const SectionHeader = styled.div`
    margin-bottom: 12px;
    flex-shrink: 0;
`;

const SectionTitleRow = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 4px;
`;

const ModeSelector = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: 16px;
`;

const ModeLabel = styled.span`
    font-size: 0.75rem;
    font-weight: 500;
    color: ${color_text_default};
    min-width: 24px;
    text-align: center;
`;

const SectionTitle = styled.h3`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 1rem;
    font-weight: 600;
    color: ${color_text_default};
    margin: 0 0 4px 0;
`;

const SectionSubtitle = styled.p`
    font-size: 0.75rem;
    color: ${color_text_light};
    margin: 0;
    font-style: italic;
`;

const IncludeIcon = styled.span`
    color: ${color_colors_growth};
    font-weight: bold;
    font-size: 1rem;
`;

const ExcludeIcon = styled.span`
    color: ${color_colors_decline};
    font-weight: bold;
    font-size: 1rem;
`;

const TagCheckboxItem = styled.div<{ selected: boolean; disabled: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    border-radius: 6px;
    margin-bottom: 2px;
    background: ${props => {
        if (props.disabled) return color_shades_light;
        if (props.selected) return color_shades_lighter;
        return 'transparent';
    }};
    border: 1px solid transparent;
    opacity: ${props => props.disabled ? '0.5' : '1'};
    
    &:hover {
        background: ${props => {
        if (props.disabled) return color_shades_light;
        if (props.selected) return color_shades_lighter;
        return color_shades_lighter;
    }};
    }

    &:last-child {
        margin-bottom: 0;
    }
`;

const Checkbox = styled.input<{ disabled?: boolean }>`
    width: 16px;
    height: 16px;
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    margin: 0;
    flex-shrink: 0;
`;

const LoadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    gap: 16px;
`;

const LoadingSpinner = styled.div`
    width: 40px;
    height: 40px;
    border: 3px solid ${color_shades_light};
    border-top: 3px solid ${color_colors_growth};
    border-radius: 50%;
    animation: spin 1s linear infinite;

    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
`;

const LoadingText = styled.div`
    font-size: 0.875rem;
    color: ${color_text_light};
`;

const TagsList = styled.div<{ position?: 'left' | 'right' }>`
    border: ${style_border_default};
    border-radius: 8px;
    padding: 8px;
    background: ${color_defaults_white};
    min-height: 200px;
    height: 0; /* Allow flex-grow to work properly */
    flex-grow: 1; /* Take up available space */
    overflow-y: auto; /* Enable vertical scrolling */
    overflow-x: hidden; /* Prevent horizontal scrolling */
    
    /* Custom scrollbar styling */
    &::-webkit-scrollbar {
        width: 8px;
    }
    
    &::-webkit-scrollbar-track {
        background: ${color_shades_lighter};
        border-radius: 4px;
    }
    
    &::-webkit-scrollbar-thumb {
        background: ${color_shades_darker};
        border-radius: 4px;
    }
    
    &::-webkit-scrollbar-thumb:hover {
        background: #555;
    }

    @media (max-width: 768px) {
        min-height: 150px;
    }
`;

const NoTagsMessage = styled.div`
    text-align: center;
    padding: 32px 16px;
    color: ${color_text_light};
    font-size: 0.875rem;
    font-style: italic;
`;

const TagText = styled.span<{ selected: boolean; disabled: boolean }>`
    font-size: 0.9rem;
    color: ${props => {
        if (props.disabled) return color_text_light;
        if (props.selected) return color_text_default;
        return color_text_default;
    }};
    font-weight: ${props => props.selected ? '500' : 'normal'};
    flex: 1;
`;

const ResetButton = styled(Button)`
    min-width: 80px;
`;

const CancelButton = styled(Button)`
    min-width: 80px;
`;

const ApplyButton = styled(Button)`
    min-width: 120px;
`;

const PaginationFooter = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 8px 0;
    border-top: ${style_border_default};
    margin-top: 8px;
    flex-shrink: 0;
`;

const PaginationControls = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const PaginationButton = styled.button`
    padding: 4px 8px;
    border: 1px solid ${color_shades_darker};
    background: ${color_defaults_white};
    color: ${color_text_default};
    font-size: 0.75rem;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
        background: ${color_shades_lighter};
        border-color: ${color_shades_darker};
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        background: ${color_shades_light};
    }
`;

const PageInfo = styled.span`
    font-size: 0.75rem;
    color: ${color_text_default};
    font-weight: 500;
    margin: 0 4px;
`;
